// Epic/sub-issue breakdown — the server-gated "split this ticket" step that
// follows a `splitRecommended` size estimate. The model proposes a split of the
// parent's verified acceptance criteria into child tickets; the server
// validates coverage against ITS OWN split of the persisted green (a COVER —
// every criterion in AT LEAST one child; overlap is allowed, an uncovered
// criterion is the hard failure), mints a
// `breakdownToken` (scoped HMAC, mirroring `acceptToken`), and a server-only
// executor later reconstructs every GitLab write from the persisted state with
// per-child receipts so a retry resumes instead of duplicating.
//
// GitLab CE reality: no epics API, no work-item hierarchy writes. The "epic"
// is a regular issue carrying `EPIC_LABEL` + `relates_to` links to the
// children + a `## Sub-issues` task-list section (GitLab renders the checkbox
// progress natively). Everything epic-shaped lives in THIS module — the
// implement gate, the apply executor, the detail endpoint, and the web app all
// import the one predicate/parser; no scattered label-string literals.

import { z } from "zod";

import {
  extractKnownSection,
  extractMarkedBlock,
  stripKnownSection,
  stripMarkedBlock,
} from "../issue-body/parse.js";
import { BACKLINK_LINE_RE, H, MARK } from "../issue-body/tokens.js";
import { locBucketSchema, sizingCriterionSchema } from "./sizing.js";

// ---------------------------------------------------------------------------
// Acceptance-criteria enumeration — the ONE deterministic splitter shared by
// the sizing gate, the breakdown gate, the child-body renderer, and the UI, so
// "AC3" can never mean different rows to different layers.
// ---------------------------------------------------------------------------

const stripLeadingMarker = (line: string): string =>
  line.replace(/^\s*(?:[-*+]|\d+[.)])\s+/u, "").trim();

/**
 * Split a single-line value on INLINE enumeration markers — `(1) … (2) …`,
 * `1. … 2. …`, `1) … 2) …` — returning one row per criterion, or `[]` when the
 * line isn't an inline enumeration. Guards against false splits:
 *   - a marker must be followed by whitespace, so decimals/versions like
 *     "HTTP 1.1" / "2.0" (digit after the dot) never match;
 *   - only a run that STARTS at 1 and increments (1,2,3,…) counts, so a stray
 *     "2." mid-sentence can't start a row.
 * (No regex lookbehind — Safari 14 ships this module via apps/web.)
 */
const splitInlineEnumeration = (text: string): string[] => {
  const re = /(^|\s)(?:\((\d{1,2})\)|(\d{1,2})[.)])\s/gu;
  const marks: { textStart: number; markStart: number; n: number }[] = [];
  for (const m of text.matchAll(re)) {
    const idx = m.index ?? 0;
    const lead = (m[1] ?? "").length; // the leading (^|\s) boundary
    marks.push({
      markStart: idx + lead,
      textStart: idx + m[0].length,
      n: Number(m[2] ?? m[3] ?? "0"),
    });
  }
  const run: typeof marks = [];
  for (const mk of marks) if (mk.n === run.length + 1) run.push(mk);
  if (run.length < 2) return [];
  const rows: string[] = [];
  for (let i = 0; i < run.length; i += 1) {
    const stop = i + 1 < run.length ? run[i + 1]!.markStart : text.length;
    const row = text
      .slice(run[i]!.textStart, stop)
      .trim()
      .replace(/[;,.]$/u, "")
      .trim();
    if (row.length > 0) rows.push(row);
  }
  return rows;
};

/**
 * A bare label line — a short run of letters ending in a colon with NOTHING
 * after it, e.g. `Acceptance criteria:` or `Definition of done:`.
 *
 * Matched by SHAPE, not against the heading constants, so it holds for a
 * localized heading and for a user who types their own. A real criterion that
 * happens to contain a colon ("Given: the user is signed in") has content after
 * it and is untouched.
 * (No regex lookbehind — Safari 14 ships this module via apps/web.)
 */
const BARE_LABEL_LINE = /^\p{L}[\p{L} _-]{0,40}:$/u;

/**
 * The trimmed, non-empty lines of a value with a leading bare heading dropped.
 *
 * Exported because `draftRowsOf` (`./draft.ts`) has to make the SAME heading
 * decision one step EARLIER than `splitAcceptanceCriteria` does — it must know
 * whether what remains is one line before it decides whether an inline
 * enumeration may split it — and a second copy of the rule would let the two
 * disagree about what a heading is.
 *
 * Only the FIRST line, and only when rows follow it: a lone `Something:` is a
 * degenerate value, not a heading, and dropping it would turn a one-row answer
 * into an empty list.
 */
export const withoutBareHeadingLine = (
  lines: ReadonlyArray<string>,
): string[] =>
  lines.length >= 2 && BARE_LABEL_LINE.test(lines[0]!)
    ? lines.slice(1)
    : [...lines];

/**
 * Drop a leading bare heading line from a list-shaped value, when rows follow.
 *
 * The list widget serialises as `Acceptance criteria:\n- a\n- b`
 * (`buildListAnswer`) and the whole block is the grounded value, so the
 * heading travels with the rows everywhere the VALUE goes. That is right for
 * grounding — the user's turn contains those exact bytes — and wrong at the one
 * place the value is rendered under a section heading of its own, where it reads
 *
 *     ## Acceptance criteria
 *     Acceptance criteria:
 *     - …
 *
 * on the written issue. Same rule `splitAcceptanceCriteria` applies (first line
 * only, and only when rows follow it, so a one-line `Something:` value is left
 * alone); the rows themselves are never touched.
 */
export const stripBareHeadingLine = (value: string): string => {
  const nl = value.indexOf("\n");
  if (nl < 0) return value;
  if (!BARE_LABEL_LINE.test(value.slice(0, nl).trim())) return value;
  const rest = value.slice(nl + 1);
  return rest.trim().length > 0 ? rest : value;
};

/**
 * Split the verified `acceptance_criteria` green value into indexed rows: one
 * row per criterion, list markers (`- `, `* `, `+ `, `1. `, `1) `) stripped.
 *
 * Multi-line values (the AC list-builder widget, or a markdown list) split one
 * row per line. A single-line value that carries an inline enumeration
 * ("(1) … (2) …", typed in chat) is split on the markers too — otherwise five
 * criteria collapse to one, which silently vetoes the size gate
 * (`criteriaCount < 2`) and makes any breakdown impossible.
 *
 * A leading bare heading is dropped. `buildListAnswer` sends the widget's rows
 * as `Acceptance criteria:\n- row one\n- row two`, and the agent is invited to
 * quote that block contiguously — so without this the heading is persisted as
 * criterion 1. It greens (the heading really is in the user's turn), which is
 * what makes the corruption silent: it inflates `criteriaCount`, feeds the size
 * gate a phantom criterion, and shifts every `coveredCriteria` index in the
 * breakdown partition by one.
 * (No regex lookbehind — Safari 14 ships this module via apps/web.)
 */
export const splitAcceptanceCriteria = (value: string): string[] => {
  const all = value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const lines = withoutBareHeadingLine(all);
  if (lines.length >= 2) {
    return lines.map(stripLeadingMarker).filter((line) => line.length > 0);
  }
  const raw = lines[0] ?? "";
  const inline = splitInlineEnumeration(raw);
  if (inline.length >= 2) return inline;
  const single = stripLeadingMarker(raw);
  return single.length > 0 ? [single] : [];
};

// ---------------------------------------------------------------------------
// The canonical list-answer format. Lives here, not in `apps/web`, because the
// SERVER now composes it too: a park re-writes the acceptance-criteria green
// from the rows the user kept, and it must produce bytes identical to what the
// list-builder widget would have sent — a hand-rolled `- ` join server-side
// silently forks the canonical value format that `splitAcceptanceCriteria`
// (right below) and the grounding scan both depend on.
//
// GROUNDING PLANE — byte-exact English by contract. Never localize.
// ---------------------------------------------------------------------------

export const AC_HEADING = "Acceptance criteria";
export const DOD_HEADING = "Definition of done";

/** ALL rows in ONE newline-joined message. The gate's normalizer collapses the
 *  newlines, so the agent can quote a contiguous span of this single turn. */
export const buildListAnswer = (
  heading: string,
  rows: ReadonlyArray<string>,
): string =>
  [
    `${heading}:`,
    ...rows
      .map((r) => r.trim())
      .filter((r) => r !== "")
      .map((r) => `- ${r}`),
  ].join("\n");

/** The heading `buildListAnswer` should use for a list_builder on this field. */
export const listHeadingForField = (field: string): string =>
  field === "dod_criteria" ? DOD_HEADING : AC_HEADING;

/**
 * The canonical form of a list-shaped answer: the same rows, re-rendered as the
 * one serialisation every consumer reads back.
 *
 * WHY IT HAS TO EXIST. `splitAcceptanceCriteria` and `buildListAnswer` are not
 * inverses on arbitrary input, and the server-side list write
 * (`listFieldEditMutate`) refuses anything they do not round-trip — which is
 * right, because `value`, `evidenceQuote` and the echoed user turn all have to
 * be the SAME bytes, and every downstream consumer (apply's description, the
 * sizing enumeration, the breakdown partition) reads the rows back through
 * `splitAcceptanceCriteria`. The shape the client routinely produces and the
 * pair does not round-trip is a row carrying an EMBEDDED NEWLINE ("Given
 * X\nWhen Y") — plain Enter in the row editor inserts one, and so does a paste.
 * It serialises as one row and re-splits as two. Refusing that dropped the edit
 * back onto the model re-propose path, which is the #1078 row loss the server
 * write exists to remove, and it did so silently.
 *
 * IT IS LOSSLESS OR IT IS NOTHING — and the first version of this function was
 * neither. The rows are taken by SPLITTING ON NEWLINES and stripping one list
 * marker per line: every character the user typed survives into some row, which
 * is what makes re-rendering them a canonicalisation rather than an edit.
 * Deriving them through `splitAcceptanceCriteria` instead looked equivalent and
 * was not: on a SINGLE row carrying an inline enumeration that splitter takes
 * the `(1) … (2) …` branch, which starts at the first marker and DISCARDS the
 * lead-in — "Der Export unterstützt (1) PDF und (2) CSV." came back as
 * "PDF und" + "CSV", and those were the bytes echoed as the user's own turn and
 * written to the issue. Deleting three words of a criterion is the defect this
 * whole path exists to remove, so the postcondition below is the function:
 * `next` must read back as exactly the rows that went in, or the input is
 * returned untouched and the write refuses (today's behaviour, and honest).
 *
 * SCOPED TO TEXT THAT IS ALREADY A LIST ANSWER: the first line must be this
 * field's own canonical heading, which is what `buildListAnswer` emits and the
 * only shape the write path can accept anyway. Free prose is NOT quietly turned
 * into a one-row list — the echoed turn is the user's own words, and a heading
 * plus a bullet they never typed is not a canonicalisation of anything.
 */
export const canonicalListAnswer = (field: string, text: string): string => {
  const heading = listHeadingForField(field);
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l !== "");
  // The heading has to be a line of its own: `startsWith` alone also matches
  // "Acceptance criteria: something", where line 0 is a value and re-emitting it
  // as a row would print the heading twice.
  if (lines.length < 2 || lines[0] !== `${heading}:`) return text;
  const rows = lines
    .slice(1)
    .map(stripLeadingMarker)
    .filter((r) => r !== "");
  if (rows.length === 0) return text;
  const next = buildListAnswer(heading, rows);
  // THE POSTCONDITION. Not "did it reach a fixed point" — that is satisfied by a
  // lossy split as happily as by a faithful one — but "does the splitter every
  // consumer uses read these exact rows back out". The single-row inline
  // enumeration is the case this refuses, and refusing it is the only answer
  // that neither rewrites the user's sentence nor lies to the partition about
  // how many criteria there are.
  const readBack = splitAcceptanceCriteria(next);
  if (readBack.length !== rows.length) return text;
  for (let i = 0; i < rows.length; i += 1) {
    if (readBack[i] !== rows[i]) return text;
  }
  return next;
};

/**
 * Is `sub` a subsequence of `full` (same order, no new elements)?
 *
 * The one-way door under `parkCriteriaMutate`: bytes may only LEAVE the
 * acceptance-criteria green through a park. Row-wise grounding
 * (`groundUserSlotValue`, shape `"list"`) verifies every row independently
 * against the user's turns, so a strict subtraction re-grounds against the same
 * turns with no re-scan — but only if it really is a subtraction.
 */
export const isSubsequence = (
  sub: ReadonlyArray<string>,
  full: ReadonlyArray<string>,
): boolean => {
  let i = 0;
  for (const row of full) {
    if (i < sub.length && sub[i] === row) i += 1;
  }
  return i === sub.length;
};

/** The prompt/tool-facing ids for `n` criteria: `["AC1", …, "AC<n>"]`. */
export const criterionIdsFor = (count: number): string[] =>
  Array.from({ length: count }, (_, i) => `AC${String(i + 1)}`);

/** Parse "AC<k>" → zero-based index, or null when malformed / out of range. */
export const criterionIdToIndex = (
  id: string,
  count: number,
): number | null => {
  const m = /^AC([1-9]\d*)$/u.exec(id.trim());
  if (!m) return null;
  const index = Number(m[1]) - 1;
  return index < count ? index : null;
};

// ---------------------------------------------------------------------------
// `## Build notes` markers.
//
// PARSE PLANE — the heading and both markers are frozen English in every
// locale, exactly like `SECTION_HEADINGS`. Only the bold labels inside the
// block are localized (`BUILD_NOTES_LABELS`, @loom/shared/i18n).
//
// Spelled in `@loom/shared/issue-body`, which owns every heading and marker a
// body is made of; re-exported here under their existing names.
// ---------------------------------------------------------------------------

export const BUILD_NOTES_HEADING = H.buildNotes;
export const BUILD_NOTES_BEGIN = MARK.buildNotesBegin;
export const BUILD_NOTES_END = MARK.buildNotesEnd;

// ---------------------------------------------------------------------------
// Epic marker + `## Sub-issues` section. The section is the durable,
// GitLab-native representation of the parent→children relationship; the
// `relates_to` links are the queryable one. Both are written by the apply
// executor; both are re-derivable from GitLab alone (no Loom store needed on a
// return visit).
// ---------------------------------------------------------------------------

/** Marker label for a split parent. Mirrors `loom::needs-refinement` styling. */
export const EPIC_LABEL = "loom::epic";

export const isEpicLabelled = (
  labels: ReadonlyArray<string> | null | undefined,
): boolean => (labels ?? []).includes(EPIC_LABEL);

export const SUB_ISSUES_HEADING = H.subIssues;

/**
 * Remove any existing `## Sub-issues` section from an issue body.
 *
 * The trailing `<!-- loom:dor-reason: … -->` marker goes with it. The regex this
 * replaces swallowed the marker only when the task-list happened to be the last
 * section; both callers append content after this call, so on every other shape
 * the marker was stranded mid-body where the end-anchored strip could no longer
 * see it and the raw comment leaked into the editor. The writers re-stamp the
 * current one LAST.
 */
export const stripSubIssuesSection = (body: string): string =>
  stripKnownSection(body, "sub_issues");

/**
 * Extract the raw `## Sub-issues` section (heading included), or null.
 *
 * On the shared parser's span logic now. Two things that changes, both of them
 * the reason the module exists: the heading is LINE-ANCHORED, so a plan's
 * `### Sub-issues` is no longer read as the real task-list (the retired
 * `SUB_ISSUES_SECTION_RE` was unanchored and matched inside it — a plan could
 * forge a child row into the next apply's carry-over); and a trailing
 * DoR-reason marker is taken off before the scan instead of riding inside the
 * extracted section.
 */
export const extractSubIssuesSection = (body: string): string | null =>
  extractKnownSection(body, "sub_issues");

/**
 * Collapse a child title onto ONE line.
 *
 * A GitLab issue title is single-line by contract, but Loom embeds it in its
 * own markdown twice — the parent's task-list row below and every sibling's
 * `piece 3, "{title}"` build-note row — where a line break does not merely look
 * odd: it splits the row, so `parseSubIssueIids` loses the child and the build
 * note reads as a dangling fragment. Applied at the LAST mile so it holds for
 * both authors of a title, the user (typed-op lane) and the model
 * (`propose_breakdown` / `propose_breakdown_ops`).
 */
export const singleLineTitle = (title: string): string =>
  title.replace(/\s+/gu, " ").trim();

/** Render the section: a GitLab task-list, one unchecked box per child. */
export const renderSubIssuesSection = (
  children: ReadonlyArray<{ readonly iid: number; readonly title: string }>,
): string =>
  [
    SUB_ISSUES_HEADING,
    "",
    ...children.map(
      (c) => `- [ ] #${String(c.iid)} — ${singleLineTitle(c.title)}`,
    ),
  ].join("\n");

/** Parse child iids out of a body's `## Sub-issues` task-list (checked or
 *  unchecked). Server-side consumers only — the web app receives the parsed
 *  list from the detail endpoint. */
export const parseSubIssueIids = (body: string | null | undefined): number[] => {
  if (!body) return [];
  const section = extractSubIssuesSection(body);
  if (!section) return [];
  const iids: number[] = [];
  for (const line of section.split("\n")) {
    const m = /^\s*-\s+\[[ xX]\]\s+#(\d+)\b/u.exec(line);
    if (m) iids.push(Number(m[1]));
  }
  return iids;
};

// ---------------------------------------------------------------------------
// `## Build notes` — the server-composed block in a CHILD issue body.
// (Constants at the top of this file; see the note there for why.)
//
// EXTRACTION is marker-delimited, not heading-delimited, and that is the point.
// The two section regexes this repo already ships (`SUB_ISSUES_SECTION_RE`
// above, `DOD_SECTION_RE` in apply-update-issue) are unanchored and greedy: a
// `## ` heading anywhere in a body is fair game for them, and one more
// `##\s+Name` pattern would be one more way for a user sentence to be vacuumed
// into — or to truncate — a machine-owned section. Two `indexOf`s cannot do
// that.
//
// PLACEMENT is heading-FIRST, marker second — `## Build notes` on the line
// before `<!-- loom:build-notes -->`. Every OTHER reader of an issue body in
// this system is heading-delimited (`parseBody` in the dashboard editor splits
// on `\n## `; `SUB_ISSUES_SECTION_RE` runs to the next `## `), and a bare
// marker line is not a boundary to any of them: with the marker first, it is
// simply the last line of whatever section precedes the block. In a composed
// child that section is `## Acceptance criteria`, so the editor showed
// `<!-- loom:build-notes -->` to the user AS a criterion and wrote it back to
// GitLab on Save — one more junk row, and one more stray marker, per save.
// A heading is a boundary to all of them, so the block terminates the section
// above it instead of joining it.
//
// The consequence is that the heading lives OUTSIDE the markers, which is why
// both helpers below extend the span back over it: the block still has to
// round-trip WHOLE through `preserveLoomSections` / `applyFromVerifiedStore`,
// which re-append exactly what was extracted.
// ---------------------------------------------------------------------------

/**
 * The whole block — its `## Build notes` heading, both markers and everything
 * between them — or null.
 *
 * FIRST begin, LAST end: a stray marker pasted into user prose can then only
 * WIDEN the extracted span, never truncate it — so a round-trip through the
 * editor cannot silently drop the tail of a section the server owns.
 *
 * The same primitive `parseIssueBody` lifts the block with, so the two entry
 * points can never disagree about where a block begins and ends.
 */
export const extractBuildNotesSection = (
  body: string | null | undefined,
): string | null =>
  extractMarkedBlock(
    body,
    BUILD_NOTES_BEGIN,
    BUILD_NOTES_END,
    BUILD_NOTES_HEADING,
  );

/** Remove the block from a body (heading and both markers included). */
export const stripBuildNotesSection = (body: string): string =>
  stripMarkedBlock(
    body,
    BUILD_NOTES_BEGIN,
    BUILD_NOTES_END,
    BUILD_NOTES_HEADING,
  );

// The repo-path smell (`REPO_PATH_TEXT_RE`) lives with the one jargon lexicon.
export { REPO_PATH_TEXT_RE } from "../prose-audit/lexicon.js";

/** Parse the child-side backlink (`_Part of #12_`) the apply executor appends
 *  to every created child's description. Returns the parent iid or null. */
export const parseEpicParentIid = (
  body: string | null | undefined,
): number | null => {
  const m = body ? BACKLINK_LINE_RE.exec(body) : null;
  return m ? Number(m[1]) : null;
};

/**
 * The whole child-side backlink LINE, with its trailing ` — <parent title>`.
 *
 * The fourth server-owned part of an issue body, and the last one a rebuild
 * still deleted. It sits BEFORE any `## ` heading, which is exactly what makes
 * it invisible to every heading-delimited reader: `parseBody`
 * (apps/web/src/dashboard/issueDraft.ts) drops it, `preserveLoomSections`
 * restores it on a manual Save, and the refine apply — which rebuilds the whole
 * description from the card's verified greens — had no carry for it at all, so
 * an Accept on a split child stranded that child with no parseable parent while
 * the GitLab link and the epic label still said it had one.
 *
 * Matched by LINE so the title survives the round-trip; no lookbehind (this
 * module ships to the browser). `.` does not match `\r`, so a CRLF body's
 * carriage return falls outside the match and is not carried into a body
 * written with `\n`. `BACKLINK_LINE_RE` lives in `@loom/shared/issue-body` with
 * every other token a body is made of, and `parseEpicParentIid` above now reads
 * the same LINE rather than any `_Part of #N_` a model wrote mid-sentence.
 */
export const extractEpicBacklinkLine = (
  body: string | null | undefined,
): string | null => (body ? (BACKLINK_LINE_RE.exec(body)?.[0] ?? null) : null);

// ---------------------------------------------------------------------------
// propose_breakdown INPUT (model-facing). Children reference criteria by the
// SAME "AC<n>" ids the sizing prompt enumerated; estimates are NOT accepted
// from the model — the executor derives each child's estimate from the
// persisted `sizeEstimate` per-criterion mapping (distinct files of its
// covered criteria; bucket = max of covered buckets). Plain `z.object`.
// ---------------------------------------------------------------------------

/** A child's model-authored text is DERIVED, not quoted: the requirement is the
 *  server-appended `## Acceptance criteria` block (verbatim rows of the parent's
 *  verified green) — this is the scope LINE that names the slice. Bounded because
 *  the card's two-step confirm is only a real decision about bytes the user can
 *  actually read: 240 chars renders whole in the review row's 3-line clamp;
 *  8000 was a spec document nobody saw.
 *
 *  ENFORCED IN THE HANDLER (`executeProposeBreakdown` → `child_text_too_long`),
 *  not by the input schema below — see `CHILD_SCOPE_WIRE_MAX`. */
export const CHILD_SCOPE_MAX = 240;
/** Renders whole in the card row (see .bd-name) and in a GitLab list. Same
 *  handler-enforced arrangement as `CHILD_SCOPE_MAX`. */
export const CHILD_TITLE_MAX = 140;

/**
 * Wire ceilings — deliberately LOOSER than the product bounds above, and
 * deliberately the exact values this schema carried before `CHILD_*_MAX`
 * existed (255 title / 8000 description).
 *
 * A `.max()` here is checked by the MCP SDK BEFORE the tool handler runs, so an
 * over-long child comes back as a raw `input_validation_failed` — a wire error
 * with no repair guidance, which also skips the server's entire degrade chain
 * (`child_text_too_long` deny → repair in-turn, or the length attempt cap →
 * `readiness_breakdown_unavailable` → a plain Accept). Holding the ceiling at
 * the previous values means an agent that composed its proposal against the
 * older tool schema — one mid-breakdown across a deploy — still reaches the
 * handler and gets an actionable deny instead of a dead turn.
 *
 * The product bound is NOT weakened by this: the handler rejects anything over
 * `CHILD_TITLE_MAX` / `CHILD_SCOPE_MAX`, so no over-length child is ever
 * persisted, echoed, or written to GitLab.
 */
export const CHILD_TITLE_WIRE_MAX = 255;
export const CHILD_SCOPE_WIRE_MAX = 8000;

/** The build note the MODEL authors: how this piece is built, in words. Bounded
 *  because it rides the confirm card — the user reads every model-authored byte
 *  that reaches a sub-issue body before pressing Create. Handler-enforced
 *  (`child_text_too_long`), like `CHILD_SCOPE_MAX`. */
export const CHILD_APPROACH_MAX = 400;
/** Wire ceiling — looser on purpose, same rationale as `CHILD_SCOPE_WIRE_MAX`:
 *  an over-long approach must reach the handler and get a repairable deny, not a
 *  raw `input_validation_failed` that skips the whole degrade chain. */
export const CHILD_APPROACH_WIRE_MAX = 4000;

export const breakdownChildInputSchema = z.object({
  /** Model-local id ("c1"…), echoed on progress events so the UI can key rows. */
  localId: z.string().min(1).max(20),
  title: z
    .string()
    .min(1)
    .max(CHILD_TITLE_WIRE_MAX)
    .describe(
      `Imperative one-sentence title, max ${String(CHILD_TITLE_MAX)} characters` +
        " — the server rejects a longer one with a repairable deny naming every" +
        " over-long child; it does not trim it for you.",
    ),
  /** Child body WITHOUT the criteria section — the executor appends the
   *  covered criteria verbatim (server-split rows) and the parent backlink.
   *  Must contain no repo paths (executor-enforced, privacy invariant). */
  description: z
    .string()
    .min(1)
    .max(CHILD_SCOPE_WIRE_MAX)
    .describe(
      `Scope line, max ${String(CHILD_SCOPE_MAX)} characters — the server` +
        " rejects a longer one with a repairable deny naming every over-long" +
        " child. Do not restate the acceptance criteria: the server appends them" +
        " verbatim.",
    ),
  /** "AC<n>" ids this child covers. The union across children must COVER the
   *  parent's criteria — every criterion in at least one child, overlap allowed
   *  where a piece genuinely spans one. Validated server-side. */
  coveredCriteria: z.array(z.string().min(1).max(10)).min(1),
  /** How this piece is BUILT, in words — the seam it lands on and any decision
   *  the implementer should not have to re-take. Path-free (same
   *  `REPO_PATH_TEXT_RE` gate as title/description); the file list under
   *  `## Build notes` is added server-side from the sizing map.
   *
   *  OPTIONAL on the wire so an older bridge image still reaches the handler
   *  (the omission degrades to a body with no "How to build it" block, not to a
   *  dead turn). */
  approach: z
    .string()
    .max(CHILD_APPROACH_WIRE_MAX)
    .optional()
    .describe(
      `One to three sentences, max ${String(CHILD_APPROACH_MAX)} characters —` +
        " how this piece is built. Words only: no repo paths or file names. The" +
        " user reads it on the confirm card and it is written into the" +
        " sub-issue body. Omit it rather than guessing.",
    ),
});
export type BreakdownChildInput = z.infer<typeof breakdownChildInputSchema>;

export const proposeBreakdownInputShape = {
  issueIid: z.number().int().positive(),
  /** True → sizing concluded one ticket suffices; `children` must be empty. */
  noSplit: z.boolean().default(false),
  /** Why split / why not — shown on the card. Plain language, no paths. */
  rationale: z.string().min(1).max(1000),
  children: z.array(breakdownChildInputSchema).default([]),
  /**
   * `localId`s this call is CARRYING FORWARD UNCHANGED. For each one the server
   * splices in the PERSISTED row — title, description and covered criteria —
   * and discards whatever text this call wrote for it.
   *
   * This is the byte-identity channel the tool did not have. Before it, "pieces
   * 1, 2 and 4 are unchanged" was not a proposition the server could express,
   * observe or enforce: a one-of-five edit and a full regeneration arrived as
   * the IDENTICAL tool call, and any drift in the untouched four was silently
   * accepted as the new truth. That is the whole mechanism behind a "remove
   * piece 3" turn coming back with five re-worded pieces.
   *
   * Deliberately a `localId` list rather than a diff: an old bridge image omits
   * the field entirely, defaults to `[]`, and gets exactly today's behaviour.
   *
   * The splice happens BEFORE partition validation (see `executeProposeBreakdown`)
   * — validating the model's declared shape and then persisting a different one
   * would let a call pass COVER with criteria the persisted rows do not carry.
   */
  unchanged: z.array(z.string().min(1).max(24)).max(12).default([]),
} as const;
export const proposeBreakdownInputSchema = z.object(proposeBreakdownInputShape);
export type ProposeBreakdownInput = z.infer<typeof proposeBreakdownInputSchema>;

// ---------------------------------------------------------------------------
// Persisted breakdown — `CardState.breakdown`. Like `implementationPlan` and
// `sizeEstimate` it lives OUTSIDE `fields` (the acceptToken slot hash never
// moves). The per-child receipts stamped during apply ARE the durable progress
// log: a resume skips every child that already has one.
// ---------------------------------------------------------------------------

export const breakdownChildEstimateSchema = z.object({
  /** Distinct files across this child's covered criteria (server-derived). */
  filesTouched: z.number().int().nonnegative(),
  /** Max bucket across this child's covered criteria (server-derived). */
  locBucket: locBucketSchema,
});
export type BreakdownChildEstimate = z.infer<
  typeof breakdownChildEstimateSchema
>;

/** Shape of a server-minted child id: `ch_` + 10 hex. */
export const CHILD_ID_RE = /^ch_[0-9a-f]{10}$/u;

/**
 * FNV-1a, four independently-seeded 32-bit lanes → 32 hex chars.
 *
 * Deliberately NOT `node:crypto`: this module ships to the browser (apps/web
 * imports `splitAcceptanceCriteria` from the readiness barrel), and the ONE
 * import of `node:crypto` anywhere under `packages/shared` would break the Vite
 * bundle. Nothing here is a security boundary — `childId` is an addressable
 * name, `scopeSig` a "did this text move" marker. Every value that authorises
 * anything (`acceptToken`, `breakdownToken`, `childrenHash`) is still HMAC-SHA256
 * minted server-side and is not computed here.
 */
const fnv1a128 = (input: string): string => {
  const seeds = [0x811c9dc5, 0x01000193, 0x7fffffff, 0x2545f491];
  const lanes = seeds.map((seed) => {
    let h = seed >>> 0;
    for (let i = 0; i < input.length; i += 1) {
      h ^= input.charCodeAt(i);
      // h *= 16777619, in 32-bit shift-add form (Math.imul keeps it exact).
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
  });
  return lanes.map((h) => h.toString(16).padStart(8, "0")).join("");
};

/**
 * Server-minted, revision-stable child identity. Deterministic in
 * `(sessionId, issueIid, seq)` so the same seq always yields the same id — a
 * resumed apply and a re-emit agree without either carrying a counter.
 *
 * `localId` stays what it always was: the model's own render key and the
 * apply-time receipt join key. `childId` is what a TYPED OP addresses and what
 * the server copies bytes forward by, and it must outlive a revision — which
 * `localId` cannot, because the model re-mints it on every proposal.
 */
export const mintChildId = (
  sessionId: string,
  issueIid: number,
  seq: number,
): string =>
  "ch_" + fnv1a128(`${sessionId}:${String(issueIid)}:${String(seq)}`).slice(0, 10);

/**
 * The scope signature for a piece: its covered criterion TEXTS, NFC-normalized,
 * trimmed, sorted, and joined under a separator that cannot occur in a criterion
 * row (rows are single lines — `splitAcceptanceCriteria` splits on newlines).
 *
 * Sorted so a pure re-ordering is not a change; text-keyed so a park's index
 * renumbering is not a change either.
 */
export const computeScopeSig = (criterionTexts: ReadonlyArray<string>): string =>
  fnv1a128(
    [...criterionTexts]
      .map((t) => t.normalize("NFC").trim())
      .sort()
      .join("\n"),
  );

/** Separators inside the partition-signature preimage. Control characters
 *  because both halves of a row are free text: a plain concatenation of sorted
 *  rows aliases (`["ab","c"]` and `["a","bc"]` hash the same), and a space or a
 *  newline is something a title can contain. */
const TITLE_SIG_SEP = "\u0001";
const PIECE_SEP = "\u0002";

/**
 * The signature of a whole PARTITION — what a split-level rationale is about.
 *
 * `computeScopeSig` answers "does this piece still deliver the same work?" and
 * is what keeps a per-piece build note (`approach`) from outliving its scope.
 * The `rationale` is one level up: it is prose about the SHAPE — how many pieces
 * there are, what each one is called, and which work sits in which. So it needs
 * its own signature over exactly that: the piece count, and per piece its title
 * plus the criterion texts it covers.
 *
 * Sorted and text-keyed for the same two reasons `computeScopeSig` is: a pure
 * re-ordering of the pieces is not a reshaping, and parking a criterion
 * renumbers every index after it without changing what any piece promises. The
 * happy consequence is that a park followed by an un-park onto the same piece
 * reproduces the signature exactly — a change of mind restores the rationale
 * instead of retiring it for good.
 *
 * Descriptions are deliberately OUT: the only op that rewrites a scope line
 * (`edit_scope`) arrives through `propose_breakdown_ops`, which re-authors the
 * rationale in the same call. That is a SCHEMA invariant, not a convention —
 * `edit_scope` is a member of `modelOpSchema` and deliberately not of
 * `clientOpSchema`, so no zero-model-turn frame can reach a description. The
 * client's way to re-word one is `rewrite_scope`, which routes to the model.
 */
export const computePartitionSig = (
  children: ReadonlyArray<{
    readonly title: string;
    readonly coveredCriteria: ReadonlyArray<number>;
  }>,
  criteria: ReadonlyArray<string>,
): string =>
  fnv1a128(
    [
      String(children.length),
      ...children
        .map(
          (c) =>
            `${c.title.normalize("NFC").trim()}` +
            TITLE_SIG_SEP +
            computeScopeSig(
              c.coveredCriteria
                .map((i) => criteria[i] ?? "")
                .filter((t) => t !== ""),
            ),
        )
        .sort(),
    ].join(PIECE_SEP),
  );

/**
 * Does `breakdown.rationale` still describe the partition that is about to be
 * written?
 *
 * The typed client ops (rename / remove / merge / move / park / un-park) are
 * executed by the SERVER with zero model turns,
 * so no op re-authors the model's split-level prose. Live, that
 * shipped three sub-issues each stating the work "was split into four pieces".
 * The rationale is model prose about a shape, so it is governed exactly like
 * `approach`: it survives only while the shape it was authored against is the
 * shape being written.
 *
 * `rationaleSig === undefined` is TRUSTED — a row persisted before the field
 * existed has nothing to compare against, which is today's behaviour rather
 * than a retroactive suppression of every in-flight card.
 */
export const isRationaleCurrent = (
  breakdown: {
    readonly rationaleSig?: string;
    readonly children: ReadonlyArray<{
      readonly title: string;
      readonly coveredCriteria: ReadonlyArray<number>;
    }>;
  },
  criteria: ReadonlyArray<string>,
): boolean =>
  breakdown.rationaleSig === undefined ||
  breakdown.rationaleSig === computePartitionSig(breakdown.children, criteria);

/**
 * `localId` for a SERVER-created child (`add_child`, `split_further`). Held in
 * a namespace the model cannot mint into (`breakdownChildInputSchema.localId`
 * admits any string, but the gate rejects this prefix from the model), because
 * `stampChild` joins creation receipts on `localId` and a collision would stamp
 * one child's `createdIid` onto another's row — the one failure the receipts
 * design exists to prevent.
 */
export const SERVER_LOCAL_ID_PREFIX = "srv_";
export const serverLocalId = (childId: string): string =>
  `${SERVER_LOCAL_ID_PREFIX}${childId.slice(3)}`;
export const isServerLocalId = (localId: string): boolean =>
  localId.startsWith(SERVER_LOCAL_ID_PREFIX);

export const breakdownChildSchema = z.object({
  localId: z.string(),
  /** Server-minted stable identity (see `mintChildId`). OPTIONAL: a row written
   *  before ids existed must still parse — `ensureChildIds` backfills it. */
  childId: z.string().optional(),
  /**
   * A hash (FNV-1a-128, see `computeScopeSig` — deliberately not sha256, this
   * module ships to the browser) over this piece's covered criterion TEXTS,
   * sorted + NFC-normalized, as of the turn that authored `title`/`description`.
   *
   * TEXT, not indices, and that is the whole point: parking a criterion
   * renumbers every index without changing what any piece promises, so an
   * index-based signature would declare every surviving piece stale for a change
   * no reader can see — and a design that regenerates stale prose would have
   * re-created the very defect it exists to prevent.
   */
  scopeSig: z.string().optional(),
  // Deliberately UNBOUNDED, unlike `breakdownChildInputSchema`. This is the
  // PERSISTED shape: a row written before the input bound existed must still
  // parse, or an in-flight proposal is orphaned mid-apply (receipts and all).
  // The bound belongs on the MINT path only — do not tidy the two into
  // agreement.
  title: z.string(),
  description: z.string(),
  /**
   * Model-authored "how to build it", path-free (`REPO_PATH_TEXT_RE`-gated like
   * title/description), echoed on the confirm card, written into the child body
   * under `## Build notes`. Deliberately unbounded here — persisted shape, same
   * rule as `title`/`description` above.
   *
   * Cleared by any op that moves this piece's covered set: `applyBreakdownOps`
   * keeps it only while the piece's covered criterion TEXTS are unchanged (a
   * postcondition on the recomputed `scopeSig`, not N per-handler clears). A
   * build note that outlives the scope it described is a lie the implementer
   * cannot detect.
   */
  approach: z.string().optional(),
  /**
   * Server-stamped: this piece HAD a build note and an op retired it.
   *
   * Without it the two reasons a child body carries no "How to build it" are
   * indistinguishable at apply time — the model never wrote one, or the reducer
   * dropped the one it wrote because the piece's scope moved. Live, the second
   * case shipped a sub-issue that was silently missing the section its siblings
   * all had, which reads as a bug rather than as the deliberate refusal it is.
   * Set only where the note is cleared, cleared again the moment a new note
   * lands, so it can never claim a retirement that did not happen.
   *
   * Deliberately NOT in `canonicalChildrenHash`'s preimage: it is derived from
   * the absence of `approach`, not bytes the user confirmed, and every field
   * added to that preimage kills in-flight `breakdownToken`s across a deploy.
   */
  approachRetired: z.boolean().optional(),
  /** Zero-based indices into `splitAcceptanceCriteria(<verified AC green>)`. */
  coveredCriteria: z.array(z.number().int().nonnegative()),
  estimate: breakdownChildEstimateSchema,
  /** Repo paths (union over covered criteria). PERSISTED ONLY — never echoed
   *  in tool output or bus events (planMarkdown privacy contract). */
  files: z.array(z.string()),
  // ---- server-stamped creation receipts (never model-writable) ----
  createdIid: z.number().int().positive().optional(),
  createdUrl: z.string().optional(),
  linkedAtMs: z.number().int().nonnegative().optional(),
});
export type BreakdownChild = z.infer<typeof breakdownChildSchema>;

export const BREAKDOWN_STATUSES = [
  /** Sizing/agent concluded a single ticket suffices (noSplit). */
  "not_needed",
  /** Valid proposal persisted; token minted; awaiting user confirm. */
  "proposed",
  /** Apply claimed the token; child writes in flight. */
  "creating",
  "applied",
  /** A write failed mid-run; receipts stay; the same token resumes. */
  "failed",
] as const;
export type BreakdownStatus = (typeof BREAKDOWN_STATUSES)[number];

/**
 * A criterion the USER took out of scope at the split step.
 *
 * NOT a hole in COVER: the row is also gone from the `acceptance_criteria`
 * green, so there is nothing left to cover. This is what makes "do not build
 * this at all" expressible — before it, the only representable answer to
 * "remove this piece" was to redistribute its criteria onto the survivors,
 * which is exactly how one removal re-carved an entire split.
 *
 * `text` is the verbatim row and `groundedAtSeq` the turn it originally
 * grounded against, so un-parking restores the exact bytes and re-greens with
 * no re-scan — a PM's "drop EntraID" almost always means "not this sprint", and
 * a one-way retirement turns a change of mind into an unexplained red slot.
 */
export const parkedCriterionSchema = z.object({
  text: z.string(),
  groundedAtSeq: z.number().int().nonnegative(),
  parkedAtSeq: z.number().int().nonnegative(),
  parkedAtMs: z.number().int().nonnegative(),
  /**
   * This row's SIZING, held for the trip.
   *
   * The rebase matches sizing rows to criteria by text and drops what it cannot
   * match, so an un-parked criterion came back with no sizing — permanently.
   * `sizing.criteria.length` then read one short of the card's green, which the
   * client reads as `sizingStale` FOREVER (`useBreakdownCard`), so `sizing`
   * went null and the tail re-fired a billed sizing turn on a card nothing had
   * really changed. Carrying the row makes a net-zero park/un-park actually
   * net-zero. OPTIONAL — rows parked before this existed simply have none, i.e.
   * today's behaviour.
   */
  sizing: sizingCriterionSchema.optional(),
  /**
   * The POSITION this row held in the acceptance-criteria list when it left.
   *
   * Un-parking used to APPEND, which made a change of mind quietly destructive:
   * the list came back re-ordered, so `criteriaFingerprintOf` differed from the
   * pre-park value even though not one byte of any row had changed. Two things
   * ride that fingerprint. `restatePlanForCriteria` takes its `confirmed →
   * stale` leg, silently dropping the `## Implementation details` section the
   * user had already confirmed — the exact data loss its own docblock claims a
   * park/un-park is safe from. And `applyBreakdownOps`' caller re-runs the DoR
   * judge over a "changed" criteria list, whose degrade path then clears
   * `allGreen` on a finished card.
   *
   * OPTIONAL: rows parked before this existed have no index and append exactly
   * as they do today.
   */
  parkedAtIndex: z.number().int().nonnegative().optional(),
});
export type ParkedCriterion = z.infer<typeof parkedCriterionSchema>;

/**
 * An OPEN revision ticket: the server asked the model to interpret a free-text
 * adjustment into typed ops, and named which children it may author prose for.
 * While one is live the replace-whole leg is refused (`use_ops_tool`).
 */
export const breakdownRevisionSchema = z.object({
  revisionHash: z.string(),
  expMs: z.number().int().nonnegative(),
  /** childIds the model may author NEW prose for. Empty ⇒ interpretation only. */
  releasedChildIds: z.array(z.string()).max(12),
  /** True when the ticket came from `split_further` (the one op that may
   *  legitimately raise the child count past the current partition). */
  allowNew: z.boolean(),
  openedAtMs: z.number().int().nonnegative(),
});
export type BreakdownRevision = z.infer<typeof breakdownRevisionSchema>;

export const cardBreakdownSchema = z.object({
  children: z.array(breakdownChildSchema).max(12),
  /** sha256 of the parent AC green value at propose time — staleness belt:
   *  apply refuses when the current green no longer hashes to this. */
  criteriaFingerprint: z.string(),
  criteriaCount: z.number().int().nonnegative(),
  rationale: z.string(),
  /**
   * `computePartitionSig` of the partition `rationale` was authored against.
   *
   * The split-level equivalent of a child's `scopeSig`, and it exists for the
   * identical reason. The typed client ops re-shape the partition with zero
   * model turns, so nothing re-writes the model's prose about the shape: a
   * remove + un-park shipped three sub-issues each stating the work "was split
   * into four pieces". Stamped by whichever call AUTHORED the sentence
   * (`propose_breakdown`, `propose_breakdown_ops`) and deliberately NOT
   * re-stamped by `handleReadinessBreakdownOp` — a rationale nobody re-wrote for
   * the new shape is exactly what must stop being published.
   *
   * OPTIONAL, and absent means TRUSTED: a row persisted before this field
   * existed has nothing to compare against, so it behaves as it does today
   * rather than losing its rationale on a deploy.
   */
  rationaleSig: z.string().optional(),
  status: z.enum(BREAKDOWN_STATUSES),
  /**
   * WRITE-ONLY. The live counter is `CardState.breakdownAttempts`; this field is
   * stamped on persist and read by nothing. Kept because rows written before the
   * move still carry it and the schema must parse them.
   *
   * PARTITION deny counter — a bad partition is a judgement failure and gets
   * `MAX_BREAKDOWN_ATTEMPTS` tries before the step degrades to a plain Accept.
   *
   * Persisted and read, which it was not before: the gate's counter lived in
   * `registerProposeBreakdownTool`'s closure, and `buildSessionMcpServer` is
   * called INSIDE the `POST /mcp` route handler (the documented stateless
   * server+transport-per-request pattern), so the counter was rebuilt on every
   * single tool call. `attempt_cap` had therefore never fired in production and
   * the adapter's deny tap was dead code. The unit tests pass a shared Map
   * explicitly, which is why CI was green.
   */
  attempts: z.number().int().nonnegative(),
  /**
   * WRITE-ONLY, like `attempts` above — `CardState.breakdownAttempts.childText`
   * is the live one.
   *
   * CHILD-TEXT deny counter, on its own budget. An over-long title/scope line
   * is a formatting slip the model repairs verbatim from the deny, so it must
   * not spend the partition budget on typography — three ordinary "remove piece
   * 3" rounds have to stay affordable after one. It cannot be unbounded either:
   * the wire schema deliberately admits over-long text so the handler can answer
   * with a repairable deny, which makes this a real loop unless something counts
   * it. OPTIONAL: rows written before the counter was persisted lack it.
   */
  childTextAttempts: z.number().int().nonnegative().optional(),
  /** Monotonic seq feeding `mintChildId`. OPTIONAL: absent on pre-ops rows;
   *  `ensureChildIds` seeds it from the backfill. */
  nextChildSeq: z.number().int().nonnegative().optional(),
  /** `2` ⇒ this card's partition is addressable by typed ops (every child
   *  carries a `childId`). Absent ⇒ the legacy replace-whole-only shape. */
  assignmentVersion: z.literal(2).optional(),
  /** Criteria the user retired at the split step. See `parkedCriterionSchema`. */
  parked: z.array(parkedCriterionSchema).max(60).optional(),
  /** Open model-revision ticket. See `breakdownRevisionSchema`. */
  pendingRevision: breakdownRevisionSchema.optional(),
  breakdownToken: z.string().optional(),
  breakdownTokenExpMs: z.number().int().nonnegative().optional(),
  /** sha256 over canonical children (minus receipts) + criteriaFingerprint —
   *  what the token authorises. A re-propose rotates it (stale token dies). */
  childrenHash: z.string().optional(),
  /** Single-use apply claim — claim-then-RESUME (never released; the same
   *  token drives the idempotent remainder after a partial failure). */
  consumedAtMs: z.number().int().nonnegative().optional(),
  /** Receipt: the parent `## Sub-issues` + EPIC_LABEL write landed. */
  parentSectionAppendedAtMs: z.number().int().nonnegative().optional(),
  failureReason: z.string().optional(),
  createdAtMs: z.number().int().nonnegative(),
  updatedAtMs: z.number().int().nonnegative(),
});
export type CardBreakdown = z.infer<typeof cardBreakdownSchema>;
